import type { CaseImageRecord } from '@/api/caseImages'
import type { CaseImageVariantListItem } from '@messenger/types'
import {
  pickCaseImageDisplayUrl,
  pickNewestAcceptedUpscale,
  pickShownUpscale,
} from '@messenger/utils'

/** What the grid needs to draw one tile. */
export type CaseImageTileDisplay = {
  /** The image URL the tile shows. */
  url: string
  /** The accepted enhancement being shown, or null when the tile shows the source image. */
  shownVariant: CaseImageVariantListItem | null
  /** The newest accepted enhancement, shown or not. Null when none was ever accepted. */
  acceptedVariant: CaseImageVariantListItem | null
  /**
   * True when someone chose to show the original again after accepting the
   * newest enhancement; the backend saves that on the variant, for everyone.
   */
  reverted: boolean
}

/**
 * The source image URL without any AI alteration: the MIO-enhanced copy when
 * the pipeline produced one, otherwise the upload itself. The shared rule
 * with no variants offered is exactly that fallback, so the two never drift.
 */
export const resolveSourceImageUrl = (image: CaseImageRecord): string =>
  pickCaseImageDisplayUrl(image, [])

/**
 * Decide which image a grid tile shows. Priority: newest accepted upscale
 * (unless it was reverted to the original) > MIO-enhanced copy > original
 * upload. The shown upscale comes from the shared `pickShownUpscale`, which
 * orders by createdAt itself, so the grid does not trust the API's ordering,
 * and reads the revert mark the backend saved on the variant. The editor and
 * the PDF read the backend's `displayUrl`, which applies the same rule
 * server-side (`pickCaseImageDisplayUrl` in `@messenger/utils`) to the same
 * rows, so a revert shows the original in the grid, on the canvas, in print
 * and for every other person alike.
 */
export const resolveCaseImageDisplayUrl = ({
  image,
  variants,
}: {
  image: CaseImageRecord
  variants: CaseImageVariantListItem[]
}): CaseImageTileDisplay => {
  const acceptedVariant = pickNewestAcceptedUpscale(variants)
  const shownVariant = pickShownUpscale(variants)

  if (!acceptedVariant) {
    return {
      url: resolveSourceImageUrl(image),
      shownVariant: null,
      acceptedVariant: null,
      reverted: false,
    }
  }
  if (!shownVariant) {
    return {
      url: resolveSourceImageUrl(image),
      shownVariant: null,
      acceptedVariant,
      reverted: true,
    }
  }
  return {
    url: shownVariant.url as string,
    shownVariant,
    acceptedVariant,
    reverted: false,
  }
}
